import type { FileData } from '@quesmed/types/models';
import { useState } from 'react';
import { type DropzoneOptions, useDropzone } from 'react-dropzone';
import { type Control, Controller, type FieldValues, type Path } from 'react-hook-form';
import { AiTwotoneDelete } from 'react-icons/ai';
import { GoFileZip } from 'react-icons/go';
import { GrCloudUpload } from 'react-icons/gr';
import { bytesToMB } from '@/utils';
import { cn } from '../../cn';
import { Spinner } from '../../Display/Spinner/Spinner';
import { toaster } from '../../Overlay';
import { Tooltip } from '../../Overlay/Tooltip/Tooltip';

export interface FileUploadProps extends Omit<React.ComponentProps<'div'>, 'onChange'> {
  value?: File | FileData;
  onChange: (value: File | FileData | undefined) => void;
  isInvalid: boolean;
  errorMessage?: string;
  options?: DropzoneOptions;
  description?: string;
  isRequired?: boolean;
  label?: string;
  showPreview?: boolean;
}

export const FileUpload = ({
  value,
  onChange,
  options,
  description,
  isInvalid,
  errorMessage,
  className,
  children,
  label,
  isRequired,
  showPreview = true,
  ...rest
}: FileUploadProps) => {
  const [loading, setLoading] = useState(false);

  const onUpload = async (acceptedFiles: File[]) => {
    try {
      setLoading(true);
      //   const data = jsonObjectToFormData({
      //     [FILE_KEY]: acceptedFiles[0],
      //   });
      //   const res = await axios.post<IResponse<FileData>>('content/upload/', data, {
      //     headers: { 'Access-Control-Allow-Origin': '*' },
      //   });
      onChange(acceptedFiles[0] as never);
    } catch (error) {
      toaster.error((error as Error)?.message || 'Upload Failed');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = (acceptedFiles: File[]) => {
    onUpload(acceptedFiles);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: { 'application/zip': ['.zip'], 'application/x-zip-compressed': ['.zip'] },
    minSize: 0,
    maxFiles: 1,
    disabled: loading,
    ...options,
  });

  const onRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    /**TODO:: Add an remove api to remove file from server */
    onChange(undefined);
  };

  return (
    <div className={cn(className, 'flex flex-col space-y-1')}>
      {label && (
        <span className='text-sm text-foreground pb-1'>
          {label} {isRequired && <span className='text-danger'>*</span>}
        </span>
      )}
      <div
        {...getRootProps()}
        className={cn(
          'border-2 border-default-200 bg-default-100 hover:border-default-400',
          'rounded-md h-full cursor-pointer transition-colors duration-300 flex justify-center items-center',
          { 'cursor-not-allowed': loading },
        )}
        {...rest}
      >
        <input
          className='relative z-50 block h-full w-full cursor-pointer p-20 opacity-0'
          multiple={false}
          {...getInputProps()}
        />
        <div className='flex flex-col justify-center items-center p-4'>
          {loading ? (
            <Spinner classNames={{ label: 'text-sm' }} label='Uploading...' size='lg' />
          ) : value && showPreview ? (
            <div className='flex flex-col space-y-1 text-foreground text-center justify-center items-center'>
              <GoFileZip className='w-10 h-10' />
              <div className='flex justify-center items-center space-x-1'>
                <Tooltip
                  classNames={{
                    content: 'text-xs rounded-sm',
                  }}
                  placement='bottom'
                  content='Remove'
                >
                  <button
                    type='button'
                    onClick={onRemove}
                    className='hover:bg-default-200 rounded-full p-1 w-6 h-6'
                  >
                    <AiTwotoneDelete className='w-full h-full' />
                  </button>
                </Tooltip>
                <span className='text-xs max-w-44 w-full truncate'>Click bin icon to remove file</span>
              </div>
            </div>
          ) : (
            children || <GrCloudUpload className='text-foreground w-10 h-10' />
          )}
        </div>
      </div>

      {value && !isInvalid && showPreview ? (
        <div className='flex justify-between space-x-4 items-center'>
          <span className='text-xs text-foreground-400'>{(value as File)?.name}</span>
          <span className='text-xs text-foreground-400'>{bytesToMB((value as File)?.size ?? 1)} MB</span>
        </div>
      ) : (
        (description || errorMessage) && (
          <span
            className={cn('text-xs w-full text-foreground-400', {
              'text-danger-500': isInvalid && errorMessage,
            })}
          >
            {errorMessage || description}
          </span>
        )
      )}
    </div>
  );
};

export interface FormFileInputProps<T extends FieldValues>
  extends Omit<FileUploadProps, 'value' | 'onChange' | 'isInvalid'> {
  name: Path<T>;
  control: Control<T, unknown>;
  onUpload?: (file: File | FileData | undefined) => void;
}

export const FormFileInput = <T extends FieldValues>({
  control,
  name,
  onUpload,
  ...rest
}: FormFileInputProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error, invalid } }) => (
        <FileUpload
          {...rest}
          value={value}
          onChange={(file) => {
            onChange(file);
            onUpload?.(file);
          }}
          isInvalid={invalid}
          errorMessage={error?.message}
        />
      )}
    />
  );
};
